/**
 * ============================================================================
 * Iron Meridian - Main App Component
 * ============================================================================
 * 
 * Root component that manages global game state and renders the game layout.
 * Shows main menu when no active game, transitions to game layout when playing.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import GameLayout from './components/GameLayout'
import MainMenu from './components/MainMenu'
import AudioKeyboardHandler from './components/AudioKeyboardHandler'
import TurnSummaryModal from './components/TurnSummaryModal'
import TutorialOverlay from './components/TutorialOverlay'
import AutoSaveIndicator, { saveStatus } from './components/AutoSaveIndicator'
import { useCombatFeedback } from './components/CombatFeedback'
import useTurnSummary from './hooks/useTurnSummary'
import FullGameReplay from './components/FullGameReplay'
import { audioManager } from './audio/AudioManager.js'
import { getGameState, getCountries, startNewGame, getDiplomacyState, proposeTreaty, acceptProposal, rejectProposal, cancelTreaty, getPendingProposals, progressResearch, loadDiplomacyState, saveDiplomacyState, initializeDiplomacyFromNewGame, generateDiplomacyFromCountries, processDiplomacyEndOfTurn, clearDiplomacyLogs, resetTechState } from './services/api'
import * as db from './services/persistence/db.js'
import * as saveService from './services/saveService.js'

const LOCAL_SAVE_KEY = 'ironmeridian.singleplayerSave.v1'

function loadLocalSave() {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(LOCAL_SAVE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || !parsed.gameState || !parsed.countries) return null
    return parsed
  } catch {
    return null
  }
}

function writeLocalSave(payload) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LOCAL_SAVE_KEY, JSON.stringify(payload))
  } catch {
    // Ignore quota/security errors
  }
}

function clearLocalSave() {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(LOCAL_SAVE_KEY)
  } catch {
    // Ignore
  }
}

function App() {
  // Core game state
  const [gameState, setGameState] = useState(null)
  const [countries, setCountries] = useState({})
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [actionQueue, setActionQueue] = useState([])
  const [combatLog, setCombatLog] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [turnProcessing, setTurnProcessing] = useState(false)
  const [hasSavedGame, setHasSavedGame] = useState(false)
  
  // Replay mode state
  const [replayMode, setReplayMode] = useState(null) // { gameId, initialTurn }
  
  // Diplomacy state - now uses the new data model with relationships as object
  const [diplomacyState, setDiplomacyState] = useState({ relationships: {}, pendingProposals: [], logs: [] })
  
  // Derive players list from countries (AI nations for diplomacy)
  const players = useMemo(() => {
    if (!countries || !gameState?.playerCountry) return []
    
    const playersList = []
    
    // Add human player
    playersList.push({
      id: 'player_human',
      name: `Player (${gameState.playerCountry})`,
      type: 'human',
      personality: null,
      is_eliminated: false,
      country: gameState.playerCountry
    })
    
    // Add AI-controlled countries
    for (const [countryId, country] of Object.entries(countries)) {
      if (countryId === gameState.playerCountry) continue
      if (country.controller !== 'ai') continue
      
      playersList.push({
        id: `ai_${countryId}`,
        name: country.name || countryId,
        type: 'ai',
        personality: country.personality || 'opportunist',
        is_eliminated: false,
        country: countryId
      })
    }
    
    return playersList
  }, [countries, gameState?.playerCountry])
  
  // Track previous state for turn summary
  const previousStateRef = useRef(null)
  
  // UX hooks
  const { isOpen: summaryOpen, summaryData, showSummary, closeSummary } = useTurnSummary()
  const { showVictory, showDefeat, showTerritoryGained, showTerritoryLost, CombatFeedbackEffects } = useCombatFeedback()

  // Check for saved game on load (but don't auto-load - always show main menu first)
  useEffect(() => {
    const checkForSavedGame = async () => {
      try {
        setIsLoading(true)

        // Check D1 database for saved game
        try {
          const dbResult = await db.loadGameState()
          if (dbResult.success && dbResult.data?.gameState?.status === 'active') {
            setHasSavedGame(true)
            setIsLoading(false)
            return
          }
        } catch (dbErr) {
          console.warn('[App] D1 check failed, trying localStorage:', dbErr.message)
        }

        // Check localStorage for saved game
        const local = loadLocalSave()
        if (local?.gameState?.status === 'active') {
          setHasSavedGame(true)
          setIsLoading(false)
          return
        }

        // No saved game found
        setHasSavedGame(false)
        setError(null)
      } catch (err) {
        console.error('Failed to check for saved game:', err)
        setError(null) // Don't show error, just show menu
      } finally {
        setIsLoading(false)
      }
    }
    checkForSavedGame()
  }, [])

  // Listen for replay requests from analytics or other components
  useEffect(() => {
    const handleOpenReplay = (event) => {
      const { gameId, initialTurn } = event.detail || {}
      if (gameId) {
        setReplayMode({ gameId, initialTurn: initialTurn || 0 })
      }
    }

    window.addEventListener('openReplay', handleOpenReplay)
    return () => window.removeEventListener('openReplay', handleOpenReplay)
  }, [])

  // Auto-save to localStorage on state changes (fast, sync)
  // D1 database save only happens on END TURN to reduce API calls
  useEffect(() => {
    if (!gameState) return
    
    // Save to localStorage immediately (sync, fast)
    writeLocalSave({
      version: 1,
      savedAt: new Date().toISOString(),
      gameState,
      countries,
      selectedCountry,
      actionQueue,
      combatLog
    })
    
    // Also save diplomacy to its localStorage key for api.js functions
    if (diplomacyState?.relationships) {
      saveDiplomacyState(diplomacyState)
    }
  }, [gameState, countries, selectedCountry, actionQueue, combatLog, diplomacyState])

  // Helper function to save to D1 database (called on END TURN)
  const saveToD1 = useCallback(async (newGameState, newCountries, newDiplomacy) => {
    if (!newGameState || newGameState.status !== 'active') return
    
    saveStatus.savingStarted()
    try {
      await db.saveGameState({
        gameState: newGameState,
        countries: newCountries,
        diplomacy: newDiplomacy,
        actionQueue: [],
        combatLog
      })
      saveStatus.saveCompleted()
    } catch (err) {
      console.warn('[App] D1 save failed:', err.message)
      saveStatus.saveFailed(err)
    }
  }, [combatLog])

  // Handle country selection
  const handleCountrySelect = useCallback((countryId) => {
    setSelectedCountry(countryId)
  }, [])

  // Add action to queue
  const handleAddAction = useCallback((action) => {
    setActionQueue(prev => [...prev, { ...action, id: Date.now() }])
  }, [])

  // Remove action from queue
  const handleRemoveAction = useCallback((actionId) => {
    setActionQueue(prev => prev.filter(a => a.id !== actionId))
  }, [])

  // Clear all actions
  const handleClearActions = useCallback(() => {
    setActionQueue([])
  }, [])

  // Fetch diplomacy state - prefer localStorage for singleplayer, fallback to API
  const fetchDiplomacyState = useCallback(async () => {
    if (!gameState?.status || gameState.status !== 'active') return
    try {
      // First check localStorage for singleplayer state (new data model)
      const localState = loadDiplomacyState()
      if (localState.relationships && Object.keys(localState.relationships).length > 0) {
        setDiplomacyState(localState)
        return
      }
      
      // Fallback to API (for future multiplayer/KV support)
      const [relRes, propRes] = await Promise.all([
        getDiplomacyState(),
        getPendingProposals()
      ])
      if (relRes.success && relRes.data?.relationships) {
        const newState = {
          ...relRes.data,
          pendingProposals: propRes.success ? propRes.data?.proposals || [] : []
        }
        setDiplomacyState(newState)
        // Also save to localStorage for persistence
        saveDiplomacyState(newState)
      }
      if (propRes.success) {
        // Update pendingProposals in diplomacy state
        setDiplomacyState(prev => ({
          ...prev,
          pendingProposals: propRes.data?.proposals || []
        }))
      }
    } catch (err) {
      console.error('Failed to fetch diplomacy state:', err)
    }
  }, [gameState?.status])

  // Fetch diplomacy when game becomes active
  useEffect(() => {
    if (gameState?.status === 'active') {
      fetchDiplomacyState()
    }
  }, [gameState?.status, gameState?.currentTurn, fetchDiplomacyState])

  // Diplomacy handlers
  const handleProposeTreaty = useCallback(async (targetPlayerId, treatyType, duration) => {
    try {
      const result = await proposeTreaty(targetPlayerId, treatyType, duration)
      if (result.success) {
        fetchDiplomacyState()
      }
    } catch (err) {
      console.error('Failed to propose treaty:', err)
    }
  }, [fetchDiplomacyState])

  const handleAcceptProposal = useCallback(async (proposerPlayerId, treaty) => {
    try {
      const result = await acceptProposal(proposerPlayerId, treaty)
      if (result.success) {
        fetchDiplomacyState()
      }
    } catch (err) {
      console.error('Failed to accept proposal:', err)
    }
  }, [fetchDiplomacyState])

  const handleRejectProposal = useCallback(async (proposerPlayerId, treaty) => {
    try {
      const result = await rejectProposal(proposerPlayerId, treaty)
      if (result.success) {
        fetchDiplomacyState()
      }
    } catch (err) {
      console.error('Failed to reject proposal:', err)
    }
  }, [fetchDiplomacyState])

  const handleCancelTreaty = useCallback(async (targetPlayerId, treatyType) => {
    try {
      const result = await cancelTreaty(targetPlayerId, treatyType)
      if (result.success) {
        fetchDiplomacyState()
      }
    } catch (err) {
      console.error('Failed to cancel treaty:', err)
    }
  }, [fetchDiplomacyState])

  // Process turn
  const handleProcessTurn = useCallback(async () => {
    if (turnProcessing) return
    
    // Save previous state for turn summary comparison
    previousStateRef.current = { gameState, countries }
    
    setTurnProcessing(true)
    try {
      const response = await fetch('/api/game/turn', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          actions: actionQueue,
          gameState: gameState,
          countries: countries
        })
      })
      const result = await response.json()
      
      if (result.success) {
        const newGameState = result.data.gameState
        setGameState(newGameState)
        
        // Combat results are directly on data, not nested
        const combatResults = result.data.combatResults || []
        if (combatResults.length > 0) {
          setCombatLog(prev => [
            { turn: newGameState.currentTurn - 1, events: combatResults },
            ...prev.slice(0, 9)
          ])
          
          // Play audio AND show visual feedback for combat outcomes
          const hasPlayerVictory = combatResults.some(e => 
            (e.outcome === 'victory' || e.winner === e.attacker) && e.attacker === gameState?.playerCountry
          )
          const hasPlayerDefeat = combatResults.some(e => 
            (e.outcome === 'defeat' || e.winner !== e.attacker) && e.attacker === gameState?.playerCountry
          )
          const playerTerritoryGained = combatResults.filter(e => 
            (e.outcome === 'victory' || e.winner === e.attacker) && e.attacker === gameState?.playerCountry
          )
          const playerTerritoryLost = combatResults.filter(e =>
            (e.outcome === 'victory' || e.winner === e.attacker) && e.defender === gameState?.playerCountry
          )
          
          // Play sound and show visual feedback (priority: victory > defeat > capture)
          if (hasPlayerVictory) {
            audioManager.playEvent('combatVictory')
            showVictory('Victory!')
          } else if (hasPlayerDefeat) {
            audioManager.playEvent('combatDefeat')
            showDefeat()
          } else if (playerTerritoryGained.length > 0) {
            audioManager.playEvent('territoryCaptured')
            showTerritoryGained(playerTerritoryGained[0].defender)
          }
          
          // Show territory lost feedback
          if (playerTerritoryLost.length > 0) {
            showTerritoryLost(playerTerritoryLost[0].defender)
          }
        }
        
        setActionQueue([])
        
        // Progress research each turn (base 10 points + economy bonus)
        const playerCountry = countries[gameState?.playerCountry]
        const researchPointsPerTurn = 10 + Math.floor((playerCountry?.economy || 0) / 10)
        const researchResult = progressResearch(researchPointsPerTurn)
        if (researchResult.completed) {
          console.log('Research completed:', researchResult.completed.tech.name)
          // Could add a notification or sound here
        }
        
        // Process diplomacy at end of turn
        const diplomacyResult = processDiplomacyEndOfTurn(newGameState.currentTurn)
        if (diplomacyResult.logs && diplomacyResult.logs.length > 0) {
          console.log('Diplomacy changes:', diplomacyResult.logs)
        }
        // Refresh diplomacy state
        const updatedDiplomacy = loadDiplomacyState()
        setDiplomacyState(updatedDiplomacy)
        
        // Use countries from turn response if available, otherwise refetch
        let newCountries = countries
        if (result.data.countries) {
          newCountries = result.data.countries
          setCountries(result.data.countries)
        } else {
          const countriesRes = await getCountries()
          newCountries = countriesRes.data
          setCountries(countriesRes.data)
        }
        
        // Show turn summary modal
        showSummary(
          result.data, 
          previousStateRef.current, 
          { ...newGameState, countries: newCountries },
          newCountries,
          gameState?.playerCountry
        )
        
        // Save to D1 database after successful turn
        await saveToD1(newGameState, newCountries, updatedDiplomacy)
      }
    } catch (err) {
      console.error('Failed to process turn:', err)
      setError('Failed to process turn')
    } finally {
      setTurnProcessing(false)
    }
  }, [actionQueue, turnProcessing, gameState, countries, showVictory, showDefeat, showTerritoryGained, showTerritoryLost, showSummary, saveToD1])

  // Start new game
  const handleNewGame = useCallback(async (playerCountry = 'USA') => {
    try {
      setIsLoading(true)
      const result = await startNewGame(playerCountry)
      
      // Use countries directly from new game response
      let newGameState
      if (result.data.gameState) {
        newGameState = { ...result.data.gameState }
        setGameState(newGameState)
      } else {
        newGameState = { ...result.data }
        setGameState(newGameState)
      }
      
      if (result.data.countries) {
        setCountries(result.data.countries)
      } else {
        const countriesRes = await getCountries()
        setCountries(countriesRes.data)
      }

      // Always generate diplomacy client-side using the sophisticated diplomacy engine
      // Server diplomacy format (array-based) doesn't match client format (object keyed by iso3)
      const countriesData = result.data.countries || countries
      let diplomacy
      if (countriesData && Object.keys(countriesData).length > 0) {
        diplomacy = generateDiplomacyFromCountries(countriesData, playerCountry)
        setDiplomacyState(diplomacy)
        saveDiplomacyState(diplomacy) // Persist to localStorage
      } else {
        // Fallback: refetch countries then generate
        const countriesRes = await getCountries()
        diplomacy = generateDiplomacyFromCountries(countriesRes.data, playerCountry)
        setDiplomacyState(diplomacy)
        saveDiplomacyState(diplomacy) // Persist to localStorage
      }

      // New game replaces any previous save
      clearLocalSave()
      
      // Reset tech tree state for new game
      resetTechState()
      
      setActionQueue([])
      setCombatLog([])
      setSelectedCountry(playerCountry) // Auto-select player country
      setHasSavedGame(true) // New game counts as having a saved game
    } catch (err) {
      console.error('Failed to start new game:', err)
      setError('Failed to start new game')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Return to main menu
  const handleReturnToMenu = useCallback(() => {
    setGameState(prev => ({ ...prev, status: 'waiting' }))
    setSelectedCountry(null)
    setActionQueue([])
    setCombatLog([])
  }, [])

  // Handle load game - tries D1 first, then localStorage
  const handleLoadGame = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      // Try D1 database first
      const dbResult = await db.loadGameState()
      
      if (dbResult.success && dbResult.data?.gameState) {
        const saved = dbResult.data
        const loadedGameState = saved.gameState
        
        // If worldSeed exists but countries don't, regenerate from seed
        let loadedCountries = saved.countries || {}
        if (loadedGameState.worldSeed && (!saved.countries || Object.keys(saved.countries).length === 0)) {
          console.log('[App] Regenerating countries from worldSeed:', loadedGameState.worldSeed)
          const { buildCountries } = await import('../functions/api/game/countryData.js')
          loadedCountries = buildCountries(loadedGameState.worldSeed, {
            playerCountry: loadedGameState.playerCountry
          })
        }
        
        setGameState(loadedGameState)
        setCountries(loadedCountries)
        setSelectedCountry(saved.selectedCountry || null)
        setActionQueue(saved.actionQueue || [])
        setCombatLog(saved.combatLog || [])
        if (saved.diplomacy?.relationships) {
          setDiplomacyState(saved.diplomacy)
          saveDiplomacyState(saved.diplomacy) // Sync to localStorage
        }
        return
      }
      
      // Fall back to localStorage
      const localSaved = loadLocalSave()
      if (localSaved) {
        const loadedGameState = localSaved.gameState
        
        // If worldSeed exists but countries don't, regenerate from seed
        let loadedCountries = localSaved.countries
        if (loadedGameState.worldSeed && (!localSaved.countries || Object.keys(localSaved.countries).length === 0)) {
          console.log('[App] Regenerating countries from worldSeed:', loadedGameState.worldSeed)
          const { buildCountries } = await import('../functions/api/game/countryData.js')
          loadedCountries = buildCountries(loadedGameState.worldSeed, {
            playerCountry: loadedGameState.playerCountry
          })
        }
        
        setGameState(loadedGameState)
        setCountries(loadedCountries)
        setSelectedCountry(localSaved.selectedCountry || null)
        setActionQueue(localSaved.actionQueue || [])
        setCombatLog(localSaved.combatLog || [])
        
        // Load diplomacy from its localStorage key
        const diplomacy = loadDiplomacyState()
        if (diplomacy?.relationships) {
          setDiplomacyState(diplomacy)
        }
        return
      }
      
      setError('No saved game found')
    } catch (err) {
      console.error('Failed to load game:', err)
      setError('Failed to load saved game')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Handle loading from a save slot (campaigns/saves system)
  const handleLoadSaveSlot = useCallback((saveData) => {
    if (!saveData) {
      setError('Invalid save data')
      return
    }

    try {
      // Data can be in different formats:
      // 1. New format with flattened fields at top level (load.js returns this)
      // 2. Old format with snapshot wrapper
      const gameStateData = saveData.gameState || saveData.snapshot?.state?.gameState || saveData.snapshot?.gameState
      const countriesData = saveData.countries || saveData.snapshot?.state?.countries || saveData.snapshot?.countries
      const diplomacyData = saveData.diplomacy || saveData.snapshot?.state?.diplomacy || saveData.snapshot?.diplomacy
      const actionQueueData = saveData.actionQueue || saveData.snapshot?.state?.actionQueue || saveData.snapshot?.actionQueue || []
      const combatLogData = saveData.combatLog || saveData.snapshot?.state?.combatLog || saveData.snapshot?.combatLog || []
      
      if (!gameStateData || !countriesData) {
        console.error('Missing required data:', { 
          hasGameState: !!gameStateData, 
          hasCountries: !!countriesData,
          saveDataKeys: Object.keys(saveData)
        })
        setError('Save data is missing required fields')
        return
      }
      
      // Ensure gameState has a valid status - FORCE to active when loading a save
      const normalizedGameState = {
        ...gameStateData,
        status: 'active'  // Always set to active when loading a save
      }
      
      console.log('Loading save with gameState:', {
        originalStatus: gameStateData.status,
        normalizedStatus: normalizedGameState.status,
        currentTurn: normalizedGameState.currentTurn,
        phase: normalizedGameState.phase,
        playerCountry: normalizedGameState.playerCountry
      })
      
      // Set game state from save
      setGameState(normalizedGameState)
      setCountries(countriesData)
      setSelectedCountry(normalizedGameState.playerCountry) // Auto-select player's home country
      setActionQueue(actionQueueData)
      setCombatLog(combatLogData)
      
      // Load diplomacy if present
      if (diplomacyData?.relationships) {
        setDiplomacyState(diplomacyData)
        saveDiplomacyState(diplomacyData)
      }
      
      // Also save to D1/localStorage for auto-save continuity
      db.saveGameState({
        gameState: normalizedGameState,
        countries: countriesData,
        diplomacy: diplomacyData,
        actionQueue: actionQueueData,
        combatLog: combatLogData
      })
      
      setHasSavedGame(true)
      
      console.log('Save loaded successfully')
    } catch (err) {
      console.error('Failed to load save slot:', err)
      setError('Failed to load save')
    }
  }, [])

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-war-room-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-war-room-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl text-gray-300">Loading Iron Meridian...</h2>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-war-room-900">
        <div className="text-center p-8 bg-war-room-800 rounded-lg shadow-panel">
          <div className="text-war-room-danger text-4xl mb-4">⚠️</div>
          <h2 className="text-xl text-gray-300 mb-4">{error}</h2>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-war-room-accent hover:bg-blue-600 rounded transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  // Show main menu if no active game
  if (!gameState?.status || gameState.status === 'waiting') {
    return (
      <MainMenu
        onStartGame={handleNewGame}
        hasSavedGame={hasSavedGame}
        onLoadGame={handleLoadGame}
        onLoadSaveSlot={handleLoadSaveSlot}
      />
    )
  }

  return (
    <>
      {/* Replay Mode - takes over full screen when active */}
      {replayMode && (
        <FullGameReplay
          gameId={replayMode.gameId}
          initialTurn={replayMode.initialTurn}
          turnHighlights={[]} // TODO: Get from analytics if available
          onExit={() => setReplayMode(null)}
        />
      )}

      {/* Normal game view - hidden when replay active */}
      {!replayMode && (
        <>
          <AudioKeyboardHandler />
          <AutoSaveIndicator />
          <GameLayout
            gameState={gameState}
            countries={countries}
            selectedCountry={selectedCountry}
            actionQueue={actionQueue}
            combatLog={combatLog}
            turnProcessing={turnProcessing}
            onCountrySelect={handleCountrySelect}
            onAddAction={handleAddAction}
            onRemoveAction={handleRemoveAction}
            onClearActions={handleClearActions}
            onProcessTurn={handleProcessTurn}
            onNewGame={handleNewGame}
            onReturnToMenu={handleReturnToMenu}
            // Diplomacy props
            players={players}
            diplomacyState={diplomacyState}
            pendingProposals={diplomacyState.pendingProposals || []}
            onProposeTreaty={handleProposeTreaty}
            onAcceptProposal={handleAcceptProposal}
            onRejectProposal={handleRejectProposal}
            onCancelTreaty={handleCancelTreaty}
          />
          
          {/* Turn Summary Modal */}
          <TurnSummaryModal
            isOpen={summaryOpen}
            onClose={closeSummary}
            turnNumber={summaryData?.turnNumber}
            combatResults={summaryData?.combatResults || []}
            economyChanges={summaryData?.economyChanges || []}
            buildingsCompleted={summaryData?.buildingsCompleted || []}
            aiIntents={summaryData?.aiIntents || []}
            territoriesGained={summaryData?.territoriesGained || 0}
            territoriesLost={summaryData?.territoriesLost || 0}
            majorEvents={summaryData?.majorEvents || []}
            crisisEvents={summaryData?.crisisEvents || []}
            activeCrises={summaryData?.activeCrises || []}
            countries={countries}
          />
          
          {/* Tutorial Overlay */}
          <TutorialOverlay 
            isActive={gameState?.status === 'active'} 
          />
          
          {/* Combat Visual Effects */}
          <CombatFeedbackEffects />
        </>
      )}
    </>
  )
}

export default App
